// src/Components/FamilyTree.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Paper,
  Card,
  CardContent,
  Chip,
  IconButton,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  AccountTree as TreeIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusStrong as CenterIcon
} from '@mui/icons-material';
import Tree from 'react-d3-tree';
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  serverTimestamp,
  limit
} from 'firebase/firestore';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';

const FamilyTree = ({ familyBoardId }) => {
  const { currentUser } = useAuth();
  const theme = useTheme();
  const containerRef = useRef(null);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [relationships, setRelationships] = useState({});
  const [rootUserData, setRootUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRelationship, setEditRelationship] = useState(null);
  const [formData, setFormData] = useState({
    memberId: '',
    relationshipType: 'parent',
    memberName: ''
  });
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(0.8);
  const [treeInitialized, setTreeInitialized] = useState(false);
  const db = getFirestore();

  const relationshipTypes = [
    { value: 'grandparent', label: 'Grandparent', color: '#1b5e20' },
    { value: 'parent', label: 'Parent', color: '#2e7d32' },
    { value: 'step-parent', label: 'Step-Parent', color: '#9c27b0' },
    { value: 'spouse', label: 'Spouse', color: '#c2185b' },
    { value: 'partner', label: 'Partner', color: '#c2185b' },
    { value: 'sibling', label: 'Sibling', color: '#66bb6a' },
    { value: 'in-law', label: 'In-Law', color: '#e91e63' },
    { value: 'aunt/uncle', label: 'Aunt/Uncle', color: '#6d4c41' },
    { value: 'cousin', label: 'Cousin', color: '#689f38' },
    { value: 'niece/nephew', label: 'Niece/Nephew', color: '#8d6e63' },
    { value: 'child', label: 'Child', color: '#4caf50' },
    { value: 'step-child', label: 'Step-Child', color: '#ba68c8' },
    { value: 'grandchild', label: 'Grandchild', color: '#81c784' },
    { value: 'friend', label: 'Friend', color: '#42a5f5' },
    { value: 'other', label: 'Other', color: '#757575' }
  ];

  // Keep tree visuals identical across light/dark mode for consistent experience.
  const TREE_STYLE = {
    fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
    relationshipLabel: '#388e3c',
    linkColor: '#66bb6a',
  };

  // O(1) member lookup by ID — recomputed only when familyMembers changes
  const memberLookupMap = useMemo(() => {
    const map = new Map();
    familyMembers.forEach(m => map.set(m.id, m));
    return map;
  }, [familyMembers]);

  // Fetch all family members — capped at 100 to bound query cost
  useEffect(() => {
    const fetchFamilyMembers = async () => {
      if (!familyBoardId || !currentUser) return;

      try {
        const currentUserDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (!currentUserDoc.exists()) {
          logger.warn('Current user document not found');
          return;
        }

        const userData = currentUserDoc.data();
        const q = query(
          collection(db, 'users'),
          where('familyCode', '==', userData.familyCode || familyBoardId),
          limit(100)
        );
        const querySnapshot = await getDocs(q);
        const members = [];
        querySnapshot.forEach((d) => {
          members.push({ id: d.id, ...d.data() });
        });
        setFamilyMembers(members);
      } catch (err) {
        logger.error('Error fetching family members:', err);
        try {
          const membersQuery = query(
            collection(db, `families/${familyBoardId}/members`),
            limit(100)
          );
          const membersSnapshot = await getDocs(membersQuery);
          const members = [];
          membersSnapshot.forEach((d) => {
            members.push({ id: d.id, ...d.data() });
          });
          setFamilyMembers(members);
        } catch (fallbackError) {
          logger.error('Error fetching family members from fallback:', fallbackError);
        }
      }
    };

    fetchFamilyMembers();
  }, [db, familyBoardId, currentUser]);

  // Fetch current user's relationships — runs once on mount, not on every collapse toggle
  useEffect(() => {
    const fetchRelationships = async () => {
      if (!currentUser) return;

      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setRootUserData(userData);
          setRelationships(userData.relationships || {});
          setLoading(false);
        } else {
          setError('User profile not found');
          setLoading(false);
        }
      } catch (err) {
        logger.error('Error fetching relationships:', err);
        setError('Failed to load family tree');
        setLoading(false);
      }
    };

    fetchRelationships();
  }, [currentUser, db]);

  // Build grouped tree — pure computation, no Firestore I/O, reacts to state changes
  const treeData = useMemo(() => {
    if (!currentUser || !rootUserData) return null;
    return buildGroupedTree(currentUser.uid, relationships, rootUserData, memberLookupMap);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, relationships, rootUserData, memberLookupMap]);

  // Center tree on initial data load using actual container dimensions
  useEffect(() => {
    if (treeData && !treeInitialized && containerRef.current) {
      const { offsetWidth, offsetHeight } = containerRef.current;
      setTranslate({ x: offsetWidth / 2, y: offsetHeight * 0.18 });
      setTreeInitialized(true);
    }
  }, [treeData, treeInitialized]);

  /**
   * Build a generation-aware grouped tree from the flat relationships map.
   * Groups: Ancestors → Spouse/Partner → Siblings → In-Laws →
   *         Descendants → Extended Family → Friends & Other
   * Each group uses a rounded-rect node; person nodes use the existing leaf shape.
   */
  function buildGroupedTree(rootUserId, userRelationships, userData, memberLookup) {
    const firstName = userData.firstName || '';
    const lastName = userData.lastName || '';
    const fullName = (firstName + ' ' + lastName).trim();
    const displayName = fullName || userData.displayName || userData.email || 'You';

    const rootNode = {
      name: displayName,
      attributes: { relationship: 'You', userId: rootUserId, isRoot: true, isGroupNode: false },
      children: []
    };

    if (!userRelationships || Object.keys(userRelationships).length === 0) {
      return rootNode;
    }

    // Sort relationships into generation buckets
    const buckets = {
      grandparents: [],
      parents: [],
      stepParents: [],
      spouses: [],
      siblings: [],
      inLaws: [],
      children: [],
      stepChildren: [],
      grandchildren: [],
      auntsUncles: [],
      cousins: [],
      niecesNephews: [],
      friends: [],   // friends get their own branch
      other: []
    };

    Object.entries(userRelationships).forEach(([uid, rel]) => {
      const member = memberLookup.get(uid);
      const storedName = rel.name || '';
      const memberName = member
        ? (((member.firstName || '') + ' ' + (member.lastName || '')).trim() ||
           member.displayName ||
           member.email ||
           '')
        : '';
      const name = (storedName || memberName || 'Unknown').trim() || 'Unknown';
      const entry = { uid, name, type: rel.type };

      switch (rel.type) {
        case 'grandparent':  buckets.grandparents.push(entry);  break;
        case 'parent':       buckets.parents.push(entry);       break;
        case 'step-parent':  buckets.stepParents.push(entry);   break;
        case 'spouse':
        case 'partner':      buckets.spouses.push(entry);       break;
        case 'sibling':      buckets.siblings.push(entry);      break;
        case 'in-law':       buckets.inLaws.push(entry);        break;
        case 'child':        buckets.children.push(entry);      break;
        case 'step-child':   buckets.stepChildren.push(entry);  break;
        case 'grandchild':   buckets.grandchildren.push(entry); break;
        case 'aunt/uncle':   buckets.auntsUncles.push(entry);   break;
        case 'cousin':       buckets.cousins.push(entry);       break;
        case 'niece/nephew': buckets.niecesNephews.push(entry); break;
        case 'friend':       buckets.friends.push(entry);       break;
        default:             buckets.other.push(entry);         break;
      }
    });

    const makePersonNode = (entry) => ({
      name: entry.name,
      attributes: { relationship: entry.type, userId: entry.uid, isRoot: false, isGroupNode: false },
      children: []
    });

    const makeGroupNode = (label, members, color) => ({
      name: label,
      attributes: {
        isRoot: false,
        isGroupNode: true,
        groupColor: color,
        count: members.length
      },
      children: members.map(makePersonNode)
    });

    // --- Ancestors (grandparents + parents + step-parents) ---
    const allAncestors = [
      ...buckets.grandparents,
      ...buckets.parents,
      ...buckets.stepParents
    ];
    if (allAncestors.length > 0) {
      const hasGrandparents = buckets.grandparents.length > 0;
      const parentMembers = [...buckets.parents, ...buckets.stepParents];
      const hasParents = parentMembers.length > 0;

      if (hasGrandparents && hasParents) {
        rootNode.children.push({
          name: 'Ancestors',
          attributes: { isRoot: false, isGroupNode: true, groupColor: '#1b5e20', count: allAncestors.length },
          children: [
            makeGroupNode('Grandparents', buckets.grandparents, '#1b5e20'),
            makeGroupNode('Parents', parentMembers, '#2e7d32')
          ]
        });
      } else if (hasGrandparents) {
        rootNode.children.push(makeGroupNode('Grandparents', buckets.grandparents, '#1b5e20'));
      } else {
        rootNode.children.push(makeGroupNode('Parents', parentMembers, '#2e7d32'));
      }
    }

    // --- Spouse/Partner (shown as individual leaf nodes, not grouped) ---
    buckets.spouses.forEach(s => rootNode.children.push(makePersonNode(s)));

    // --- Siblings ---
    if (buckets.siblings.length > 0) {
      rootNode.children.push(makeGroupNode('Siblings', buckets.siblings, '#66bb6a'));
    }

    // --- In-Laws ---
    if (buckets.inLaws.length > 0) {
      rootNode.children.push(makeGroupNode('In-Laws', buckets.inLaws, '#e91e63'));
    }

    // --- Descendants (children + step-children + grandchildren) ---
    const allDescendants = [
      ...buckets.children,
      ...buckets.stepChildren,
      ...buckets.grandchildren
    ];
    if (allDescendants.length > 0) {
      const childMembers = [...buckets.children, ...buckets.stepChildren];
      const hasChildren = childMembers.length > 0;
      const hasGrandchildren = buckets.grandchildren.length > 0;

      if (hasChildren && hasGrandchildren) {
        rootNode.children.push({
          name: 'Descendants',
          attributes: { isRoot: false, isGroupNode: true, groupColor: '#4caf50', count: allDescendants.length },
          children: [
            makeGroupNode('Children', childMembers, '#4caf50'),
            makeGroupNode('Grandchildren', buckets.grandchildren, '#81c784')
          ]
        });
      } else if (hasChildren) {
        rootNode.children.push(makeGroupNode('Children', childMembers, '#4caf50'));
      } else {
        rootNode.children.push(makeGroupNode('Grandchildren', buckets.grandchildren, '#81c784'));
      }
    }

    // --- Extended Family (aunts/uncles, cousins, nieces/nephews) ---
    const extendedMembers = [
      ...buckets.auntsUncles,
      ...buckets.cousins,
      ...buckets.niecesNephews
    ];
    if (extendedMembers.length > 0) {
      const extChildren = [];
      if (buckets.auntsUncles.length > 0)
        extChildren.push(makeGroupNode('Aunts & Uncles', buckets.auntsUncles, '#6d4c41'));
      if (buckets.cousins.length > 0)
        extChildren.push(makeGroupNode('Cousins', buckets.cousins, '#689f38'));
      if (buckets.niecesNephews.length > 0)
        extChildren.push(makeGroupNode('Nieces & Nephews', buckets.niecesNephews, '#8d6e63'));

      if (extChildren.length === 1) {
        rootNode.children.push(extChildren[0]);
      } else {
        rootNode.children.push({
          name: 'Extended Family',
          attributes: { isRoot: false, isGroupNode: true, groupColor: '#795548', count: extendedMembers.length },
          children: extChildren
        });
      }
    }

    // --- Friends (own branch — supports friend-groups using the app together) ---
    if (buckets.friends.length > 0) {
      rootNode.children.push(makeGroupNode('Friends', buckets.friends, '#1565c0'));
    }

    // --- Other (truly miscellaneous) ---
    if (buckets.other.length > 0) {
      rootNode.children.push(makeGroupNode('Other', buckets.other, '#757575'));
    }

    return rootNode;
  }

  const handleSubmit = async () => {
    if (!formData.memberId) {
      setError('Please select a family member');
      return;
    }

    try {
      const selectedMember = memberLookupMap.get(formData.memberId);
      const updatedRelationships = { ...relationships };

      updatedRelationships[formData.memberId] = {
        type: formData.relationshipType,
        name: selectedMember?.displayName || selectedMember?.email || 'Unknown',
        addedAt: new Date().toISOString()
      };

      await updateDoc(doc(db, 'users', currentUser.uid), {
        relationships: updatedRelationships,
        updatedAt: serverTimestamp()
      });

      setRelationships(updatedRelationships);
      setSuccess(editRelationship ? 'Relationship updated' : 'Relationship added');
      setDialogOpen(false);
      setEditRelationship(null);
      setFormData({ memberId: '', relationshipType: 'parent', memberName: '' });
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      logger.error('Error saving relationship:', err);
      setError('Failed to save relationship');
    }
  };

  const handleDelete = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this relationship?')) return;

    try {
      const updatedRelationships = { ...relationships };
      delete updatedRelationships[memberId];

      await updateDoc(doc(db, 'users', currentUser.uid), {
        relationships: updatedRelationships,
        updatedAt: serverTimestamp()
      });

      setRelationships(updatedRelationships);
      setSuccess('Relationship removed');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      logger.error('Error deleting relationship:', err);
      setError('Failed to remove relationship');
    }
  };

  const openAddDialog = () => {
    setEditRelationship(null);
    setFormData({ memberId: '', relationshipType: 'parent', memberName: '' });
    setDialogOpen(true);
  };

  const openEditDialog = (memberId) => {
    const relationship = relationships[memberId];
    setEditRelationship(memberId);
    setFormData({
      memberId: memberId,
      relationshipType: relationship.type,
      memberName: relationship.name
    });
    setDialogOpen(true);
  };

  const getRelationshipColor = (type) => {
    const rel = relationshipTypes.find(r => r.value === type);
    return rel ? rel.color : '#2e7d32';
  };

  const getRelationshipLabel = (type) => {
    const rel = relationshipTypes.find(r => r.value === type);
    return rel ? rel.label : type;
  };

  // Organic leaf bezier path — same shape as the approved preview
  const leafPath = (sw) =>
    `M 0,${-sw} Q ${sw*0.43},${-sw*0.8} ${sw*0.5},${-sw*0.39} Q ${sw*0.57},0 ${sw*0.5},${sw*0.39} Q ${sw*0.43},${sw*0.8} 0,${sw} Q ${-sw*0.43},${sw*0.8} ${-sw*0.5},${sw*0.39} Q ${-sw*0.57},0 ${-sw*0.5},${-sw*0.39} Q ${-sw*0.43},${-sw*0.8} 0,${-sw} Z`;

  const getLeafDisplayName = (rawName) => {
    const name = (rawName || 'Unknown').trim();
    const firstName = name.split(/\s+/).filter(Boolean)[0] || 'Unknown';
    if (firstName.length <= 24) return firstName;
    return `${firstName.slice(0, 23).trimEnd()}…`;
  };

  const getLeafScaleX = (name, isRoot) => {
    // Tune around long first names so 10-12 chars fit inside curved leaf edges.
    const baseChars = isRoot ? 10 : 9;
    const scale = name.length / baseChars;
    return Math.max(1.08, Math.min(scale, 1.95));
  };

  const renderCustomNode = ({ nodeDatum, toggleNode }) => {
    const isRoot = nodeDatum.attributes?.isRoot;
    const isGroupNode = nodeDatum.attributes?.isGroupNode;
    const relationship = nodeDatum.attributes?.relationship;

    const labelColor = TREE_STYLE.relationshipLabel;

    // ── GROUP NODE: pill with count bubble and collapse toggle ──────────
    if (isGroupNode) {
      const color = nodeDatum.attributes.groupColor || '#388e3c';
      const count = nodeDatum.attributes.count || 0;
      const isCollapsed = nodeDatum.__rd3t?.collapsed;

      return (
        <g onClick={toggleNode} style={{ cursor: 'pointer' }}>
          <rect x="-60" y="-20" width="120" height="40" rx="20" fill={color} />
          <text x="-8" y="0" textAnchor="middle" fill="white"
            stroke="none" fontSize="12" fontWeight="400" fontFamily={TREE_STYLE.fontFamily} dominantBaseline="central">
            {nodeDatum.name}
          </text>
          <circle cx="40" cy="0" r="14" fill="rgba(255,255,255,0.2)" />
          <text x="40" y="0" textAnchor="middle" fill="white"
            stroke="none" fontSize="11" fontWeight="400" fontFamily={TREE_STYLE.fontFamily} dominantBaseline="central">
            {count}
          </text>
          <circle cx="56" cy="-18" r="9" fill="white" />
          <text x="56" y="-18" textAnchor="middle" fill={color}
            stroke="none" fontSize="14" fontWeight="400" fontFamily={TREE_STYLE.fontFamily} dominantBaseline="central">
            {isCollapsed ? '+' : '−'}
          </text>
        </g>
      );
    }

    // ── LEAF NODE: root (larger) or person ──────────────────────────────
    const color = isRoot ? '#2e7d32' : getRelationshipColor(relationship);
    const sw = isRoot ? 56 : 44;
    const fullName = (nodeDatum.name || 'Unknown').trim() || 'Unknown';
    const displayName = getLeafDisplayName(fullName);
    const leafScaleX = getLeafScaleX(displayName, isRoot);

    if (isRoot) {
      return (
        <g>
          <title>{fullName}</title>
          <path d={leafPath(sw)} transform={`scale(${leafScaleX},1)`} fill={color} stroke="#1b5e20" strokeWidth={1.5 / leafScaleX} />
          <text x="0" y="-4" textAnchor="middle" fill="white"
            stroke="none" fontSize="13" fontWeight="400" fontFamily={TREE_STYLE.fontFamily}>
            {displayName}
          </text>
          <text x="0" y="14" textAnchor="middle" fill="rgba(255,255,255,0.7)"
            stroke="none" fontSize="10" fontWeight="400" fontFamily={TREE_STYLE.fontFamily}>
            (You)
          </text>
        </g>
      );
    }

    return (
      <g>
        <title>{fullName}</title>
        <path d={leafPath(sw)} transform={`scale(${leafScaleX},1)`} fill={color} stroke="#1b5e20" strokeWidth={1.5 / leafScaleX} />
        <text x="0" y="-2" textAnchor="middle" fill="white"
          stroke="none" fontSize="11" fontWeight="400" fontFamily={TREE_STYLE.fontFamily}>
          {displayName}
        </text>
        <text x="0" y={sw + 14} textAnchor="middle" fill={labelColor}
          stroke="none" fontSize="11" fontWeight="400" fontFamily={TREE_STYLE.fontFamily}>
          {getRelationshipLabel(relationship)}
        </text>
      </g>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'primary.main' }}>
            Family Tree
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your personalized family tree — you are at the center
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={openAddDialog}
        >
          Add Relationship
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Tree Visualization */}
      <Paper
        ref={containerRef}
        sx={{
          mb: 4,
          height: '620px',
          position: 'relative',
          bgcolor: 'background.paper',
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: theme.shadows[4]
        }}
      >
        {treeData ? (
          <>
            {/* Zoom / center controls */}
            <Box sx={{
              position: 'absolute', top: 12, right: 12, zIndex: 10,
              display: 'flex', gap: 0.5,
              bgcolor: 'background.paper',
              border: '1px solid', borderColor: 'divider',
              borderRadius: 2, p: 0.5,
              boxShadow: 2
            }}>
              <IconButton size="small" onClick={() => setZoom(z => Math.min(z + 0.15, 2.5))}>
                <ZoomInIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => setZoom(z => Math.max(z - 0.15, 0.2))}>
                <ZoomOutIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                title="Re-center"
                onClick={() => {
                  if (containerRef.current) {
                    const { offsetWidth, offsetHeight } = containerRef.current;
                    setZoom(0.8);
                    setTranslate({ x: offsetWidth / 2, y: offsetHeight * 0.18 });
                  }
                }}
              >
                <CenterIcon fontSize="small" />
              </IconButton>
            </Box>

            <Tree
              data={treeData}
              orientation="vertical"
              translate={translate}
              zoom={zoom}
              pathFunc="diagonal"
              renderCustomNodeElement={renderCustomNode}
              separation={{ siblings: 1.4, nonSiblings: 2.0 }}
              nodeSize={{ x: 180, y: 200 }}
              styles={{
                links: {
                  stroke: TREE_STYLE.linkColor,
                  strokeWidth: 2,
                  strokeOpacity: 0.8
                }
              }}
            />
          </>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <TreeIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Your family tree is empty
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Start adding family relationships to build your tree
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openAddDialog}>
              Add First Relationship
            </Button>
          </Box>
        )}
      </Paper>

      {/* Relationships List */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
            Your Relationships
          </Typography>
          {Object.keys(relationships).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No relationships added yet. Start building your family tree by adding relationships above.
            </Typography>
          ) : (
            <List>
              {Object.entries(relationships).map(([memberId, relData]) => (
                <ListItem
                  key={memberId}
                  sx={{
                    borderLeft: `4px solid ${getRelationshipColor(relData.type)}`,
                    mb: 1,
                    bgcolor: 'background.default'
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: getRelationshipColor(relData.type) }}>
                      <PersonIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={relData.name}
                    secondary={
                      <Chip
                        label={getRelationshipLabel(relData.type)}
                        size="small"
                        sx={{
                          bgcolor: getRelationshipColor(relData.type),
                          color: 'white',
                          fontWeight: 600,
                          mt: 0.5
                        }}
                      />
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton size="small" onClick={() => openEditDialog(memberId)} sx={{ mr: 1 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(memberId)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Relationship Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editRelationship ? 'Edit Relationship' : 'Add Family Relationship'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Family Member</InputLabel>
              <Select
                value={formData.memberId}
                label="Family Member"
                onChange={(e) => setFormData({ ...formData, memberId: e.target.value })}
                disabled={!!editRelationship}
              >
                {familyMembers
                  .filter(member => member.id !== currentUser.uid && !relationships[member.id])
                  .map((member) => (
                    <MenuItem key={member.id} value={member.id}>
                      {member.displayName || member.email}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Relationship Type</InputLabel>
              <Select
                value={formData.relationshipType}
                label="Relationship Type"
                onChange={(e) => setFormData({ ...formData, relationshipType: e.target.value })}
              >
                {relationshipTypes.map((rel) => (
                  <MenuItem key={rel.value} value={rel.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: rel.color }} />
                      <Typography>{rel.label}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Alert severity="info" sx={{ mt: 1 }}>
              Define how this person is related to <strong>you</strong>. For example, select "Parent" if
              this person is your parent.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editRelationship ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FamilyTree;
